import { useEffect, useRef, useState } from "react";
import { Camera, Image as ImageIcon, X, Upload, Loader2, Check } from "lucide-react";
import { getCsrfToken } from "../lib/auth";

export type ImageCaptureSource = "photo" | "screenshot";

interface Props {
  source: ImageCaptureSource;
  scope?: string;
  onClose: () => void;
  onCaptured: () => void;
}

interface SubmitResult {
  id: string;
  ts: string;
  type: string;
  scope: string;
  mediaId: string;
  width: number | null;
  height: number | null;
  ocr: { status: "done" | "skipped" | "error" | "pending"; textChars: number; degradation: string | null };
}

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif";

/** Unified image capture flow. The Capture page renders Photo +
 *  Screenshot tiles which both open this component:
 *   - source = "photo"      : the file input carries `capture="environment"`,
 *                             so Android Chrome opens the rear camera by
 *                             default. Desktop falls through to the
 *                             standard file picker.
 *   - source = "screenshot" : no capture attribute, so Android opens the
 *                             gallery / "Recent screenshots" surface and
 *                             desktop opens the file picker. Paste-from-
 *                             clipboard (Cmd/Ctrl+V) and drag-drop both
 *                             accept image bytes too.
 *
 *  Submit POSTs multipart to /api/capture/image. The server stores the
 *  image, runs Vision OCR best-effort, and lands a capture row that
 *  the existing Recent list renders. The result includes an OCR
 *  degradation banner when the cloud key isn't configured or the
 *  scope is private, which we surface inline so the user knows the
 *  text wasn't extracted yet. */
export function ImageCapture({ source, scope = "private", onClose, onCaptured }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SubmitResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Paste-from-clipboard. The handler fires on the document so the user
  // can press Cmd/Ctrl+V anywhere on the page once the modal is open;
  // mobile Safari ignores synthetic paste events on most surfaces, so
  // this is primarily a desktop affordance. We only swallow the paste
  // when an image is found, to avoid blocking text paste into the
  // caption box.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const blob = item.getAsFile();
          if (blob) {
            event.preventDefault();
            const ts = new Date().toISOString().replace(/[:.]/g, "-");
            setFile(new File([blob], `pasted-${ts}.${(item.type.split("/")[1] || "png").toLowerCase()}`, { type: item.type }));
            setError(null);
            return;
          }
        }
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const onFile = (f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("That file is not an image. Pick a JPG, PNG, WebP, HEIC, or GIF.");
      return;
    }
    setError(null);
    setFile(f);
  };

  const submit = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("image", file);
      form.append("source", source);
      form.append("scope", scope);
      if (caption.trim()) form.append("caption", caption.trim());
      const csrf = getCsrfToken();
      const res = await fetch("/api/capture/image", {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
        body: form,
      });
      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(parseError(body, res.status));
      }
      const body = (await res.json()) as SubmitResult;
      setSuccess(body);
      onCaptured();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setCaption("");
    setSuccess(null);
    setError(null);
  };

  const titleCopy = source === "photo" ? "Capture a photo" : "Capture a screenshot";
  const subtitleCopy =
    source === "photo"
      ? "Take a new photo or pick one from your library. Add a caption for context."
      : "Pick a screenshot from your device, or drop / paste one in. Add a caption for context.";
  const Icon = source === "photo" ? Camera : ImageIcon;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-capture-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3 sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="glass w-full max-w-xl rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 sm:p-6 space-y-4">
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="inline-flex w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 items-center justify-center text-white shadow-md flex-shrink-0">
              <Icon className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <h2 id="image-capture-title" className="text-lg font-bold text-slate-900 dark:text-slate-100 truncate">
                {titleCopy}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">{subtitleCopy}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full p-1.5 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        {!success ? (
          <>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const dropped = e.dataTransfer.files?.[0];
                if (dropped) onFile(dropped);
              }}
              className={`rounded-2xl border-2 border-dashed ${
                dragOver
                  ? "border-orange-500 bg-orange-50 dark:bg-orange-900/20"
                  : "border-slate-200 dark:border-slate-700 bg-slate-50/40 dark:bg-slate-800/20"
              } transition-colors p-4 min-h-[180px] flex flex-col items-center justify-center gap-3`}
            >
              {previewUrl ? (
                <div className="w-full space-y-2">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full max-h-64 object-contain rounded-lg bg-slate-100 dark:bg-slate-900"
                  />
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span className="truncate" title={file?.name}>
                      {file?.name} {file ? `(${formatBytes(file.size)})` : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="underline text-slate-700 dark:text-slate-300"
                    >
                      Choose another
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    {source === "photo" ? "Open camera or pick a photo" : "Pick a screenshot"}
                  </button>
                  <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                    Drag and drop, or paste with Cmd/Ctrl+V.
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT}
                {...(source === "photo" ? { capture: "environment" } : {})}
                onChange={(e) => onFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>

            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-1">
                Caption (optional)
              </span>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={2}
                placeholder="What is this? Where were you? Why did you capture it?"
                className="w-full resize-none bg-white dark:bg-slate-900/40 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400/50"
              />
            </label>

            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>
                Saving to <span className="font-semibold">{scope}</span> scope.
              </span>
              {scope === "private" && (
                <span className="text-amber-700 dark:text-amber-300">
                  Private scope keeps the image local; cloud OCR is paused.
                </span>
              )}
            </div>

            {error && (
              <div className="rounded-lg border border-rose-200 dark:border-rose-700/50 bg-rose-50 dark:bg-rose-900/30 px-3 py-2 text-xs text-rose-800 dark:text-rose-200">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 text-sm font-semibold px-4 py-2 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!file || busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 transition-colors"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Capturing
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Capture
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-700/50 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-100">
              Image captured. The pipeline is processing it now.
            </div>
            {success.ocr.status === "done" && success.ocr.textChars > 0 && (
              <div className="text-xs text-slate-600 dark:text-slate-300">
                Vision OCR extracted {success.ocr.textChars.toLocaleString()} characters of text.
              </div>
            )}
            {success.ocr.degradation && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
                {success.ocr.degradation}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={reset}
                className="rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-600 text-sm font-semibold px-4 py-2 transition-colors"
              >
                Capture another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function parseError(body: string, status: number): string {
  try {
    const parsed = JSON.parse(body);
    if (parsed?.error) return String(parsed.error);
  } catch {
    /* fall through */
  }
  if (status === 401) return "Sign in again to capture.";
  if (status === 413) return "That image is too large; pick something under 25 MB.";
  if (status === 415 || status === 400) return body || "That file type is not supported.";
  return body || `Capture failed (${status}).`;
}
